import { ContentCopy } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { t } from "ttag";
import Tooltip from "../Tooltip/LightTooltip";
import useStyles from "./JsonTree.style";

interface IJsonTreeProps {
  value: any;
}

interface INodeProps {
  name?: string;
  value: any;
  isLast: boolean;
  depth: number;
  classes: any;
}

// Big payloads would otherwise render thousands of nodes at once.
const MAX_OPEN_DEPTH = 3;
const MAX_OPEN_ENTRIES = 50;

const isContainer = (value: any) => value !== null && typeof value === "object";

const renderPrimitive = (value: any, classes: any) => {
  if (value === null || value === undefined) {
    return <span className={classes.nullValue}>null</span>;
  }

  if (typeof value === "string") {
    return <span className={classes.string}>{JSON.stringify(value)}</span>;
  }

  if (typeof value === "number") {
    return <span className={classes.number}>{value}</span>;
  }

  return <span className={classes.boolean}>{String(value)}</span>;
};

const JsonNode: React.FC<INodeProps> = ({ name, value, isLast, depth, classes }) => {
  const size = isContainer(value) ? Object.keys(value).length : 0;
  const [expanded, setExpanded] = useState<boolean>(depth < MAX_OPEN_DEPTH && size <= MAX_OPEN_ENTRIES);

  const label = name !== undefined && (
    <>
      <span className={classes.key}>{name}</span>
      <span className={classes.punctuation}>: </span>
    </>
  );
  const comma = !isLast && <span className={classes.punctuation}>,</span>;

  if (!isContainer(value)) {
    return (
      <div>
        {label}
        {renderPrimitive(value, classes)}
        {comma}
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries: Array<[string, any]> = isArray
    ? value.map((item: any, index: number) => [String(index), item])
    : Object.entries(value);
  const [open, close] = isArray ? ["[", "]"] : ["{", "}"];

  if (!entries.length) {
    return (
      <div>
        {label}
        <span className={classes.punctuation}>
          {open}
          {close}
        </span>
        {comma}
      </div>
    );
  }

  return (
    <div>
      <span
        className={classes.toggle}
        role="button"
        tabIndex={0}
        aria-label={expanded ? t`Collapse` : t`Expand`}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded(!expanded);
          }
        }}
      >
        {expanded ? "▾" : "▸"}
      </span>
      {label}
      <span className={classes.punctuation}>{open}</span>
      {expanded ? (
        <>
          {entries.map(([key, item], index) => (
            <div className={classes.node} key={key}>
              <JsonNode
                name={isArray ? undefined : key}
                value={item}
                isLast={index === entries.length - 1}
                depth={depth + 1}
                classes={classes}
              />
            </div>
          ))}
          <span className={classes.punctuation}>{close}</span>
        </>
      ) : (
        <>
          <span className={classes.summary}> {isArray ? `${entries.length} items` : `${entries.length} keys`} </span>
          <span className={classes.punctuation}>{close}</span>
        </>
      )}
      {comma}
    </div>
  );
};

/** A collapsible, read-only view of a JSON value, with a button that copies it. */
const JsonTree: React.FC<IJsonTreeProps> = ({ value }) => {
  const classes: any = useStyles();
  const [copied, setCopied] = useState<boolean>(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }
    },
    []
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
      setCopied(true);

      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }

      copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard access can be refused; there is nothing useful to tell the user.
    }
  };

  return (
    <div className={classes.container}>
      <Tooltip title={copied ? t`Copied` : t`Copy`} placement="left">
        <IconButton size="small" className={classes.copyButton} aria-label={t`Copy`} onClick={handleCopy}>
          <ContentCopy className={classes.copyIcon} />
        </IconButton>
      </Tooltip>
      <JsonNode value={value} isLast depth={0} classes={classes} />
    </div>
  );
};

export default JsonTree;
